/**
 * Kickard PHASE 3.10 — Player Profile screen.
 *
 * Routes:
 *   /profile/me         — current guest (uses loadGuest().guestId)
 *   /profile/:profileId — public profile by profileSlug
 *
 * Public-only data (backend already strips trust flags / raw guestId).
 * Mobile-first, tab-based layout.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { loadGuest } from '@/lib/guestIdentity';
import { setScreen } from '@/lib/sessionTracking';
import { storage } from '@/lib/storage';
import { PremiumBackButton, PremiumHomeButton, useSafeBack } from '@/components/PremiumNav';
import { TIER_ART, crestNumeralStyle, CREST_PLACEHOLDER_STYLE } from '../lib/rankCrest';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}
function fmtPct(v) { return v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`; }
function fmtNum(v) { return v == null ? '—' : Number(v).toLocaleString(); }
function fmtDuration(s) {
  if (s == null) return '—';
  const n = Math.round(Number(s) || 0);
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  return `${m}m ${n % 60}s`;
}

// Phase 3.10.1 — human-readable public labels.
// Phase 4.0.5 — ranked matches carry an optional `seasonName` on the row;
// if present we prefer "Ranked <SeasonName>" (e.g. "Ranked Preseason"),
// otherwise fall back to "Ranked Match".
function modeLabel(mt, seasonName) {
  switch (mt) {
    case 'quick_casual': return 'Quick Match';
    case 'friend': return 'Friend Match';
    case 'bot_practice': return 'Practice';
    case 'ranked': return seasonName ? `Ranked ${seasonName}` : 'Ranked Match';
    default: return mt || '—';
  }
}
function endedByLabel(v) {
  switch (v) {
    case 'penalty': return 'Penalties';
    case 'forfeit': return 'Forfeit';
    case 'timeout': return 'Timeout';
    case 'disconnect': return 'Disconnect';
    case 'normal':
    case 'fulltime': return 'Full Time';
    default: return v || '';
  }
}
function fmtShortDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (sameDay) return `Today, ${time}`;
    const yesterday = new Date(now.getTime() - 86400000);
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' · ' + time;
  } catch { return String(iso); }
}
async function _fetch(path, { retries = 2, retryDelayMs = 450 } = {}) {
  // Wrap the fetch so transient Safari "Load failed" / network errors
  // do not surface as a permanent dead-end on Profile. Server 4xx/5xx
  // bubble up as before so real backend failures remain visible.
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${BACKEND}${path}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    } catch (e) {
      lastErr = e;
      // Only retry on network-level errors — HTTP responses already
      // returned above are not retried.
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, retryDelayMs * Math.pow(2, attempt)));
        continue;
      }
    }
  }
  throw lastErr || new Error('fetch failed');
}

// ============================================================
// Profile Premium V1 — Player Career. One component for own + public
// profiles; permissions differ (bot stats / Luck Report gating).
// ============================================================
const PP = '/profile_premium/icons';
const TROPHY = '/kickard/weekly-cup/weekly_cup_trophy.webp';
const DIVISION_ROMAN = { 1: 'I', 2: 'II', 3: 'III' };
const PP_VARS = {
  '--pp-stadium-page': 'url(/kickard/season-table/stadium_page.webp)',
  '--pp-stadium-header': 'url(/kickard/season-table/stadium_header.webp)',
  '--pp-metal': 'url(/match_result_premium/victory_v3/metal_smoke_surface.webp)',
};
const TABS = [['overview', 'Overview'], ['competitive', 'Competitive'], ['matches', 'Matches'], ['rivals', 'Rivals'], ['stats', 'Stats']];
const cap = (s) => (s ? String(s).replace(/^./, (c) => c.toUpperCase()) : '');

function Ico({ name, className = 'pp-ico' }) {
  return <img className={className} src={`${PP}/${name}.svg`} alt="" aria-hidden draggable={false} />;
}

function Crest({ player, className = 'pp-crest', testid }) {
  const ranked = player && !player.isPlacement && TIER_ART[player.tier];
  const src = ranked ? TIER_ART[player.tier] : TIER_ART.gold;
  const numeral = ranked && player.tier !== 'legend' && player.division ? DIVISION_ROMAN[Number(player.division)] : null;
  return (
    <div className={className} data-testid={testid} data-tier={ranked ? player.tier : 'none'}>
      <img src={src} alt="" aria-hidden style={ranked ? undefined : CREST_PLACEHOLDER_STYLE} draggable={false} />
      {numeral && <span className="pp-crest-numeral" aria-hidden style={crestNumeralStyle(player.tier)}>{numeral}</span>}
    </div>
  );
}

function FullName({ name, className, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" className={`pp-fullname ${className || ''}${open ? ' is-open' : ''}`} title={name}
      onClick={() => setOpen((o) => !o)} data-testid={testid} aria-label={name}>
      {name}
    </button>
  );
}

function FormChips({ form, testid, small = false }) {
  if (!form) return <span className="pp-muted" data-testid={testid}>—</span>;
  const chips = String(form).split('-').filter(Boolean);
  return (
    <span className={`pp-form${small ? ' pp-form--sm' : ''}`} data-testid={testid}>
      {chips.map((c, i) => <span key={i} className={`pp-form-bit${c === 'W' ? '' : ' is-loss'}`}>{c}</span>)}
    </span>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="pp-stat" data-testid={testid}>
      <div className="pp-stat-label">{label}</div>
      <Ico name={icon} />
      <div className="pp-stat-val">{value ?? '—'}</div>
    </div>
  );
}

function Highlight({ label, value, sub, wide, testid }) {
  return (
    <div className={`pp-highlight${wide ? ' is-wide' : ''}`} data-testid={testid}>
      <small>{label}</small>
      <strong>{value ?? '—'}</strong>
      {sub && <small>{sub}</small>}
    </div>
  );
}

function Shell({ children, onBack, footer }) {
  return (
    <div className="pp" data-testid="profile-root" style={PP_VARS}>
      <div className="pp-stage">
        {children(
          <div className="pp-top">
            <span className="kpn-slot kpn-slot--left pp-nav"><PremiumBackButton onClick={onBack} testid="profile-back" /></span>
            <div className="pp-titles">
              <div className="kst__title pp-title" data-testid="profile-title">Player Profile</div>
              <div className="kst__subtitle pp-subtitle" data-testid="profile-subtitle">Player Career</div>
            </div>
            <span className="kpn-slot kpn-slot--right pp-nav pp-nav--right"><PremiumHomeButton testid="profile-home" /></span>
          </div>,
        )}
      </div>
      <footer className="pp-footer">{footer || 'KICKARD · PLAYER CAREER'}</footer>
    </div>
  );
}

export default function ProfileScreen() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [comp, setComp] = useState(null);
  const [mySlug, setMySlug] = useState(null);
  const [err, setErr] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const initialTab = (() => {
    const q = searchParams.get('tab');
    return TABS.some(([id]) => id === q) ? q : 'overview';
  })();
  const [tab, setTabState] = useState(initialTab);
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    if (t === 'overview') next.delete('tab');
    else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const isMe = !profileId || profileId === 'me';
  const goBack = useSafeBack();
  const openProfile = (id) => id && navigate(`/profile/${encodeURIComponent(id)}`);

  const load = useCallback(async () => {
    setErr(null);
    setNotFound(false);
    setData(null);
    setComp(null);
    try {
      let id = profileId;
      if (isMe) {
        const guest = loadGuest();
        if (!guest?.guestId) {
          setErr('Guest identity not ready.');
          return;
        }
        id = guest.guestId;
        const d = await _fetch(`/api/profiles/me?guestId=${encodeURIComponent(guest.guestId)}`);
        setData(d);
      } else {
        let d = await _fetch(`/api/profiles/${encodeURIComponent(profileId)}`);
        if (!d) {
          // Ranked-only player (on the Season Table, no match-stats doc yet).
          const c = await _fetch(`/api/profiles/${encodeURIComponent(profileId)}/competitive`);
          const rp = c?.ranked?.player;
          if (!rp) { setNotFound(true); setErr('Profile not found.'); return; }
          d = { displayName: rp.displayName, isEmpty: true, profileSlug: null };
        }
        setData(d);
      }
      _fetch(`/api/profiles/${encodeURIComponent(id)}/competitive`)
        .then((c) => setComp(c || { ok: false }))
        .catch(() => setComp({ ok: false }));
    } catch (e) { setErr(String(e.message || e)); }
  }, [isMe, profileId]);

  useEffect(() => { setScreen(isMe ? 'profile_me' : 'profile_other'); load(); }, [load, isMe]);
  useEffect(() => { window.scrollTo(0, 0); }, [profileId]);

  // Luck Reports are participant-only: on someone else's profile only
  // matches against the viewer may link to a report.
  useEffect(() => {
    if (isMe) return;
    const g = loadGuest();
    if (!g?.guestId) return;
    _fetch(`/api/profiles/me?guestId=${encodeURIComponent(g.guestId)}`)
      .then((d) => setMySlug(d?.profileSlug || null))
      .catch(() => setMySlug(null));
  }, [isMe]);

  if (err) return (
    <Shell onBack={goBack}>
      {(top) => (
        <>
          {top}
          <div className="pp-panel pp-state" data-testid="profile-error-panel">
            <div className="pp-state-text pp-state-text--err" data-testid="profile-error">{err}</div>
            {!notFound && <button onClick={() => load()} data-testid="profile-retry" className="pp-btn" type="button">Retry</button>}
          </div>
        </>
      )}
    </Shell>
  );
  if (!data) return (
    <Shell onBack={goBack}>
      {(top) => (<>{top}<div className="pp-panel pp-state" data-testid="profile-loading"><div className="pp-state-text">Loading…</div></div></>)}
    </Shell>
  );

  const isEmpty = data.isEmpty || (data.totalMatches ?? 0) === 0;
  const ranked = comp?.ranked || null;
  const rp = ranked?.player || null;
  const weekly = comp?.weekly || null;

  return (
    <Shell onBack={goBack}>
      {(top) => (
        <>
          <header className="pp-hero" data-testid="profile-header">
            {top}
            <ProfileIdentity data={data} comp={comp} />
          </header>
          <nav className="pp-tabs no-scrollbar" data-testid="profile-tabs" role="tablist" aria-label="Profile sections">
            {TABS.map(([id, label]) => (
              <button key={id} role="tab" type="button" aria-selected={tab === id} data-testid={`profile-tab-${id}`}
                onClick={() => setTab(id)} className={`pp-tab${tab === id ? ' is-active' : ''}`}>
                {label}
              </button>
            ))}
          </nav>
          <div className="pp-content" role="tabpanel">
            {tab === 'overview' && <OverviewTab data={data} isEmpty={isEmpty} isMe={isMe} />}
            {tab === 'competitive' && (
              <CompetitiveTab comp={comp} ranked={ranked} rp={rp} weekly={weekly} navigate={navigate} />
            )}
            {tab === 'matches' && (
              <RecentMatchesTab profileSlug={data.profileSlug} isMe={isMe} mySlug={mySlug} onOpenOpponent={openProfile} />
            )}
            {tab === 'rivals' && <RivalsTab rivals={data.rivals || {}} h2h={data.h2h || {}} onOpen={openProfile} />}
            {tab === 'stats' && <StatsTab data={data} rp={rp} isMe={isMe} />}
          </div>
          {data.profileSlug && (
            <div className="pp-slug" data-testid="profile-slug">Profile ID · {data.profileSlug}</div>
          )}
        </>
      )}
    </Shell>
  );
}

function ProfileIdentity({ data, comp }) {
  const ranked = comp?.ranked;
  const rp = ranked?.player;
  const name = data.displayName || 'Player';
  let tierLine = null;
  let srLine = null;
  let sub = null;
  let peak = null;
  if (!comp) tierLine = <div className="pp-tier pp-muted" data-testid="profile-rank-loading">Loading rank…</div>;
  else if (!comp.ok || !ranked || ranked.status === 'ranked_disabled' || ranked.status === 'no_active_season') {
    tierLine = <div className="pp-tier pp-muted" data-testid="profile-rank-unavailable">Ranked unavailable</div>;
  } else if (!rp) {
    tierLine = <div className="pp-tier" data-testid="profile-rank-tier">UNRANKED</div>;
    sub = <div className="pp-id-sub">No ranked matches yet</div>;
  } else if (rp.isPlacement) {
    tierLine = <div className="pp-tier" data-testid="profile-rank-tier">PLACEMENT</div>;
    srLine = <div className="pp-sr" data-testid="profile-rank-placement">{rp.placementMatchesPlayed} / {rp.placementMatchesRequired} <span className="pp-unit">MATCHES</span></div>;
    sub = <div className="pp-id-sub" data-testid="profile-rank-wl">{rp.wins}W {rp.losses}L<span className="pp-dot">•</span>RANKED</div>;
  } else {
    tierLine = <div className="pp-tier" data-testid="profile-rank-tier">{String(rp.tierLabel || '').toUpperCase()}</div>;
    srLine = <div className="pp-sr" data-testid="profile-rank-sr">{rp.currentSR} <span className="pp-unit">SR</span></div>;
    sub = (
      <div className="pp-id-sub" data-testid="profile-rank-wl">
        {rp.wins}W {rp.losses}L<span className="pp-dot">•</span>WIN RATE {fmtPct(rp.winRate)}
      </div>
    );
    if (rp.highestSR) peak = <div className="pp-id-peak" data-testid="profile-rank-peak">Best: {cap(rp.highestTier)} · {rp.highestSR} SR</div>;
  }
  return (
    <div className="pp-identity">
      <Crest player={rp && comp?.ok ? rp : null} testid="profile-hero-crest" />
      <div className="pp-identity-copy">
        <h1 data-testid="profile-display-name"><FullName name={name} className="pp-name" testid="profile-display-name-btn" /></h1>
        {tierLine}
        {srLine}
        {sub}
        {peak}
      </div>
    </div>
  );
}

function OverviewTab({ data, isEmpty, isMe }) {
  const tw = data.thisWeek || {};
  const gd = data.goalDifference ?? 0;
  if (isEmpty) {
    return (
      <div data-testid="profile-overview">
        <div className="pp-panel pp-state" data-testid="profile-empty">
          <div className="pp-state-title">No matches yet</div>
          <div className="pp-state-text">
            {isMe ? 'Play your first online match to build your profile.' : 'This player has no public match statistics yet.'}
          </div>
        </div>
        {isMe && <div className="pp-mode-list" data-testid="profile-empty-modes"><PracticeVsBotCard /></div>}
      </div>
    );
  }
  return (
    <div data-testid="profile-overview">
      <section className="pp-metrics" data-testid="profile-primary-cards" aria-label="All time player statistics">
        <Stat testid="profile-total-matches" icon="matches" label="MATCHES" value={fmtNum(data.completedMatches)} />
        <Stat testid="profile-wins" icon="wins" label="WINS" value={fmtNum(data.wins)} />
        <Stat testid="profile-losses" icon="losses" label="LOSSES" value={fmtNum(data.losses)} />
        <Stat testid="profile-winrate" icon="win_rate" label="WIN RATE" value={fmtPct(data.winRate)} />
        <Stat testid="profile-gf" icon="goals_for" label="GOALS FOR" value={fmtNum(data.goalsFor)} />
        <Stat testid="profile-ga" icon="goals_against" label="GOALS AGAINST" value={fmtNum(data.goalsAgainst)} />
        <Stat testid="profile-gd" icon="goal_difference" label="GOAL DIFF" value={gd >= 0 ? `+${gd}` : String(gd)} />
        <Stat testid="profile-clean-sheets" icon="clean_sheets" label="CLEAN SHEETS" value={fmtNum(data.cleanSheets)} />
      </section>
      <section className="pp-panel pp-recent" data-testid="profile-recent-form">
        <div className="pp-section-title">RECENT FORM <span className="pp-sub">(10)</span></div>
        <FormChips form={data.recentForm10} testid="profile-form10" />
      </section>
      <section className="pp-week" data-testid="profile-this-week">
        <div className="pp-section-title">THIS WEEK</div>
        <div className="pp-week-top">
          <span><strong>{tw.matches ?? 0}</strong> matches</span>
          <em>{tw.wins ?? 0}W {tw.losses ?? 0}L</em>
          <span>{(tw.goalDifference ?? 0) >= 0 ? '+' : ''}{tw.goalDifference ?? 0} goals</span>
        </div>
        {tw.form && (
          <>
            <div className="pp-section-title pp-section-title--sm">WEEKLY FORM</div>
            <FormChips form={tw.form} testid="profile-this-week-form" />
          </>
        )}
      </section>
      <div className="pp-section-title pp-gap">CAREER HIGHLIGHTS</div>
      <section className="pp-highlights">
        <Highlight testid="profile-current-streak" label="CURRENT STREAK" value={data.currentStreak || '—'} />
        <Highlight testid="profile-longest-win-streak" label="LONGEST WIN STREAK" value={fmtNum(data.longestWinStreak)} />
        <Highlight testid="profile-penalties" label="PENALTY SHOOTOUTS" value={fmtNum(data.penaltyShootoutMatches)}
          sub={`${fmtNum(data.penaltyWins)}W ${fmtNum(data.penaltyLosses)}L · ${fmtPct(data.penaltyWinRate)}`} />
        <Highlight testid="profile-avg-match" label="AVG MATCH" value={fmtDuration(data.averageMatchDurationSeconds)} />
        {data.biggestWin && (
          <Highlight wide testid="profile-biggest-win" label="BIGGEST WIN"
            value={`${data.biggestWin.score} vs ${data.biggestWin.opponentDisplayName || '—'}`} />
        )}
      </section>
    </div>
  );
}

function CompetitiveTab({ comp, ranked, rp, weekly, navigate }) {
  if (!comp) return <div className="pp-panel pp-state" data-testid="profile-competitive"><div className="pp-state-text">Loading competitive data…</div></div>;
  if (!comp.ok) {
    return (
      <div className="pp-panel pp-state" data-testid="profile-competitive">
        <div className="pp-state-text">Competitive data is unavailable right now.</div>
      </div>
    );
  }
  const season = ranked?.season;
  const seasonName = season?.name ? String(season.name).toUpperCase() : null;
  const closed = !ranked || ranked.status === 'ranked_disabled' || ranked.status === 'no_active_season';
  const band = rp?.band;
  const pct = band && !band.isMax && band.maxSR > band.minSR
    ? Math.max(0, Math.min(100, ((rp.currentSR - band.minSR) / (band.maxSR - band.minSR)) * 100)) : band?.isMax ? 100 : 0;
  const wp = weekly?.player;
  return (
    <div data-testid="profile-competitive">
      <section className="pp-comp-grid">
        <button type="button" className="pp-feature" data-testid="profile-comp-ranked" onClick={() => navigate('/ranked')}>
          <div className="pp-kicker"><Ico name="matches" /> RANKED</div>
          {closed ? (
            <div className="pp-state-text">Ranked is closed right now.</div>
          ) : (
            <>
              <div className="pp-feature-core">
                <Crest player={rp} className="pp-crest pp-crest--sm" testid="profile-comp-crest" />
                <div>
                  {!rp && <><div className="pp-feature-name">UNRANKED</div><div className="pp-caption">No ranked matches yet</div></>}
                  {rp?.isPlacement && (
                    <>
                      <div className="pp-feature-name">PLACEMENT</div>
                      <div className="pp-feature-points" data-testid="profile-comp-placement">{rp.placementMatchesPlayed} / {rp.placementMatchesRequired}</div>
                      <div className="pp-caption">{rp.wins}W {rp.losses}L <span className="pp-sep">•</span> placement matches</div>
                    </>
                  )}
                  {rp && !rp.isPlacement && (
                    <>
                      <div className="pp-feature-name">{String(rp.tierLabel || '').toUpperCase()}</div>
                      <div className="pp-feature-points" data-testid="profile-comp-sr">{rp.currentSR} SR</div>
                      <div className="pp-caption">{rp.wins}W {rp.losses}L <span className="pp-sep">•</span> {fmtPct(rp.winRate)}</div>
                    </>
                  )}
                </div>
                <Ico name="arrow" className="pp-arr" />
              </div>
              {band && (
                <>
                  <div className="pp-progress" data-testid="profile-comp-progress"><div style={{ width: `${pct}%` }} /></div>
                  <div className="pp-range">
                    <span>{band.minSR}</span>
                    <span data-testid="profile-comp-next">{band.isMax || !band.next ? 'Max rank reached' : `Next: ${band.next.label} (${band.next.minSR} SR)`}</span>
                    <span>{band.isMax ? '' : band.maxSR}</span>
                  </div>
                </>
              )}
            </>
          )}
        </button>

        {!closed && (
          <button type="button" className="pp-feature" data-testid="profile-comp-season" onClick={() => navigate('/ranked/leaderboard')}>
            <div className="pp-kicker"><Ico name="wins" /> RANKED {seasonName}</div>
            <div className="pp-feature-core">
              <img className="pp-trophy" src={TROPHY} alt="" aria-hidden draggable={false} />
              <div>
                {rp ? (
                  <>
                    <div className="pp-feature-name">{rp.isPlacement ? 'PLACEMENT' : String(rp.tierLabel || '').toUpperCase()}</div>
                    {!rp.isPlacement && <div className="pp-feature-points">{rp.currentSR} SR</div>}
                    <div className="pp-caption pp-caption--accent" data-testid="profile-comp-season-rank">#{rp.rank} {seasonName}</div>
                  </>
                ) : <div className="pp-caption">Not on the {season?.name} table yet</div>}
              </div>
              <Ico name="arrow" className="pp-arr" />
            </div>
            {season?.name === 'Preseason' && <p className="pp-footnote">Preseason ratings may reset before Season 1.</p>}
          </button>
        )}

        <button type="button" className="pp-feature" data-testid="profile-comp-weekly" onClick={() => navigate('/ranked/weekly-cup')}>
          <div className="pp-kicker"><Ico name="wins" /> WEEKLY CUP</div>
          <div className="pp-feature-core">
            <img className="pp-trophy" src={TROPHY} alt="" aria-hidden draggable={false} />
            <div>
              {wp ? (
                <>
                  <div className="pp-feature-points pp-weekly-rank" data-testid="competitive-weekly-rank">#{wp.rank}</div>
                  <div className="pp-feature-name" data-testid="competitive-weekly-points">{wp.weeklyPoints} PTS</div>
                  <div className="pp-caption" data-testid="competitive-weekly-progress">{wp.countedMatches} / {weekly.maxCountedMatches} matches</div>
                </>
              ) : (
                <div className="pp-caption" data-testid="competitive-weekly-empty">No counted Ranked matches this week</div>
              )}
            </div>
            <Ico name="arrow" className="pp-arr" />
          </div>
        </button>
      </section>
      <div className="pp-links">
        <button type="button" className="pp-btn" data-testid="profile-view-ranked" onClick={() => navigate('/ranked')}>View Ranked →</button>
        <button type="button" className="pp-btn" data-testid="profile-view-weekly-cup" onClick={() => navigate('/ranked/weekly-cup')}>View Weekly Cup →</button>
      </div>
    </div>
  );
}

function RivalCard({ label, icon, rival, onOpen }) {
  const tid = `rival-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
    <article className="pp-rival">
      <div className="pp-section-title">{label.toUpperCase()}</div>
      {!rival ? (
        <div className="pp-rival-body pp-muted" data-testid={tid}>—</div>
      ) : (
        <button type="button" className="pp-rival-body" data-testid={tid}
          onClick={() => rival.opponentProfileSlug && onOpen(rival.opponentProfileSlug)}>
          <Ico name={icon} />
          <div>
            <div className="pp-rival-name">{rival.opponentDisplayName || '—'}</div>
            <div className="pp-rival-sub">
              {rival.matches} matches <span className="pp-sep">·</span> {rival.wins}W {rival.losses}L
              {rival.winRate != null && <> <span className="pp-sep">·</span> {fmtPct(rival.winRate)}</>}
            </div>
            {rival.recentForm && <FormChips form={rival.recentForm} small />}
          </div>
          {rival.opponentProfileSlug && <Ico name="arrow" className="pp-arr" />}
        </button>
      )}
    </article>
  );
}

function RivalsTab({ rivals, h2h, onOpen }) {
  const mb = rivals.mostBeatenOpponent;
  return (
    <div data-testid="profile-rivals">
      <section className="pp-rival-list">
        <RivalCard label="Most Played" icon="matches" rival={rivals.mostPlayedOpponent} onOpen={onOpen} />
        <RivalCard label="Nemesis" icon="goals_against" rival={rivals.nemesisOpponent} onOpen={onOpen} />
        <RivalCard label="Most Wins vs" icon="wins" rival={mb && mb.wins > mb.losses ? mb : null} onOpen={onOpen} />
      </section>
      <H2HTable h2h={h2h} onOpen={onOpen} />
    </div>
  );
}

function H2HRow({ slug, h, expanded, onToggle, onOpen }) {
  return (
    <div className="pp-h2h" data-testid={`h2h-row-${slug}`}>
      <button type="button" onClick={onToggle} className="pp-h2h-head" aria-expanded={expanded}>
        <Ico name="swords" />
        <div className="pp-h2h-main">
          <div className="pp-h2h-name">{h.opponentDisplayName || slug}</div>
          <div className="pp-h2h-record">
            {h.wins || 0}W {h.losses || 0}L <span className="pp-sep">·</span> {fmtPct(h.winRate)} <span className="pp-sep">·</span> {h.matches || 0} matches
            <span className="pp-sep">·</span> Goals {h.goalsFor || 0}–{h.goalsAgainst || 0}
          </div>
          <div className="pp-h2h-meta">
            {h.recentForm && <FormChips form={h.recentForm} small />}
            <span>Last: {fmtShortDate(h.lastPlayedAt)}</span>
            <span className="pp-h2h-toggle">{expanded ? 'Hide' : 'Details'}</span>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="pp-h2h-detail" data-testid={`h2h-detail-${slug}`}>
          <div className="pp-mini-stats">
            <div className="pp-mini"><div>Wins</div><strong>{h.wins || 0}</strong></div>
            <div className="pp-mini"><div>Losses</div><strong>{h.losses || 0}</strong></div>
            <div className="pp-mini"><div>Goals For</div><strong>{h.goalsFor || 0}</strong></div>
            <div className="pp-mini"><div>Goals Against</div><strong>{h.goalsAgainst || 0}</strong></div>
          </div>
          {h.recentForm && <div className="pp-h2h-meta">Recent H2H form: <FormChips form={h.recentForm} small /></div>}
          <button type="button" onClick={() => onOpen(slug)} className="pp-link" data-testid={`h2h-open-${slug}`}>
            Open opponent profile →
          </button>
        </div>
      )}
    </div>
  );
}

function H2HTable({ h2h, onOpen }) {
  const rows = useMemo(
    () => Object.entries(h2h || {}).map(([slug, h]) => ({ slug, ...h })).sort((a, b) => (b.matches || 0) - (a.matches || 0)),
    [h2h],
  );
  const [expanded, setExpanded] = useState(null);
  if (!rows.length) return <div className="pp-panel pp-state pp-gap"><div className="pp-state-text">No H2H records yet.</div></div>;
  return (
    <section data-testid="profile-h2h-table">
      <h2 className="pp-section-title pp-gap">HEAD-TO-HEAD</h2>
      <div className="pp-h2h-list">
        {rows.map((r) => (
          <H2HRow key={r.slug} slug={r.slug} h={r} expanded={expanded === r.slug}
            onToggle={() => setExpanded(expanded === r.slug ? null : r.slug)} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

const MODE_FILTERS = [['all', 'All modes'], ['ranked', 'Ranked'], ['quick_casual', 'Quick Match'], ['friend', 'Friend Match']];

function RecentMatchesTab({ profileSlug, isMe, mySlug, onOpenOpponent }) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState(null);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState('all');
  useEffect(() => {
    _fetch(`/api/profiles/${encodeURIComponent(profileSlug)}/matches?limit=20`)
      .then((d) => setMatches(d?.matches || []))
      .catch((e) => setErr(String(e.message)));
  }, [profileSlug]);
  const shown = (matches || []).filter((m) => mode === 'all' || m.matchType === mode);
  return (
    <div data-testid="profile-matches">
      <div className="pp-filter-row">
        <span className="pp-section-title">MATCH HISTORY</span>
        <select className="pp-filter" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="profile-matches-filter" aria-label="Filter by mode">
          {MODE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {err && <div className="pp-panel pp-state"><div className="pp-state-text pp-state-text--err">{err}</div></div>}
      {!err && matches === null && <div className="pp-panel pp-state"><div className="pp-state-text">Loading…</div></div>}
      {!err && matches && !shown.length && (
        <div className="pp-panel pp-state" data-testid="profile-matches-empty"><div className="pp-state-text">No recent matches.</div></div>
      )}
      {!err && shown.length > 0 && (
        <div data-testid="profile-recent-matches" className="pp-match-list">
          {shown.map((m) => {
            const isWin = m.result === 'win';
            const isLoss = m.result === 'loss';
            const pens = m.endedBy === 'penalty';
            const canReport = !!m.matchId && (isMe || (!!mySlug && m.opponentProfileSlug === mySlug));
            return (
              <article key={m.matchId} data-testid={`profile-recent-row-${m.matchId}`}
                className={`pp-match${isWin ? ' is-win' : isLoss ? ' is-loss' : ''}`}>
                <span className="pp-outcome">{isWin ? 'W' : isLoss ? 'L' : '·'}</span>
                <div className="pp-match-data">
                  <div className="pp-match-top">
                    <span className="pp-versus">vs</span>
                    {m.opponentProfileSlug ? (
                      <button type="button" className="pp-opp pp-opp--link" data-testid={`profile-recent-row-opp-${m.matchId}`}
                        onClick={() => onOpenOpponent(m.opponentProfileSlug)}>
                        {m.opponentDisplayName || m.opponentProfileSlug}
                      </button>
                    ) : <span className="pp-opp">{m.opponentDisplayName || '—'}</span>}
                  </div>
                  <div className="pp-match-second">
                    <b>{m.myScore}–{m.opponentScore}</b>
                    <span data-testid={`profile-recent-row-mode-${m.matchId}`}>{modeLabel(m.matchType, m.seasonName)}</span>
                    {pens && <span className="pp-tag">Penalties</span>}
                    {!pens && m.endedBy && m.endedBy !== 'normal' && m.endedBy !== 'fulltime' && <span className="pp-tag">{endedByLabel(m.endedBy)}</span>}
                    {m.matchType === 'ranked' && typeof m.srDelta === 'number' && (
                      <span data-testid={`profile-recent-row-sr-${m.matchId}`}
                        className={`pp-srchange${m.srDelta > 0 ? ' is-pos' : m.srDelta < 0 ? ' is-neg' : ''}`}>
                        {m.srDelta > 0 ? `+${m.srDelta} SR` : `${m.srDelta} SR`}
                      </span>
                    )}
                  </div>
                  <div className="pp-match-time" data-testid={`profile-recent-row-time-${m.matchId}`}>{fmtShortDate(m.completedAt)}</div>
                  {canReport && (
                    <button type="button" data-testid={`profile-recent-row-report-${m.matchId}`} className="pp-link"
                      onClick={() => navigate(`/match-report/${encodeURIComponent(m.matchId)}`, {
                        state: { origin: 'profile_matches', returnPath: `${window.location.pathname}?tab=matches` },
                      })}>
                      LUCK REPORT →
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModeCard({ icon, name, major, minor, extra, testid, children }) {
  return (
    <article className="pp-mode" data-testid={testid}>
      <Ico name={icon} />
      <div className="pp-mode-details">
        <div className="pp-mode-name">{name}</div>
        <div className="pp-mode-major">{major}</div>
        {minor && <div className="pp-mode-minor">{minor}</div>}
        {extra && <div className="pp-mode-minor">{extra}</div>}
        {children}
      </div>
    </article>
  );
}

function StatsTab({ data, rp, isMe }) {
  const ms = data.modeSplit || {};
  const modeCard = (label, m, tid, icon) => (
    <ModeCard key={tid} testid={`profile-mode-${tid}`} icon={icon} name={label.toUpperCase()}
      major={`${fmtNum(m.matches)} matches`}
      minor={`${fmtNum(m.wins)}W ${fmtNum(m.losses)}L · ${fmtPct(m.winRate)}`}
      extra={`GF ${fmtNum(m.goalsFor)} · GA ${fmtNum(m.goalsAgainst)} · GD ${(m.goalDifference ?? 0) >= 0 ? '+' : ''}${m.goalDifference ?? 0}`} />
  );
  const fun = [
    ['Most goals in a match', fmtNum(data.mostGoalsScoredInMatch)],
    ['Most conceded', fmtNum(data.mostGoalsConcededInMatch)],
    ['Failed to score', fmtNum(data.failedToScoreMatches)],
    ['One-goal wins', fmtNum(data.oneGoalWins)],
    ['One-goal losses', fmtNum(data.oneGoalLosses)],
    ['Fastest win', fmtDuration(data.fastestWinSeconds)],
    ['Longest match', fmtDuration(data.longestMatchSeconds)],
    ['Longest loss streak', fmtNum(data.longestLossStreak)],
  ];
  return (
    <div data-testid="profile-stats">
      <section data-testid="profile-stats-by-mode">
        <h2 className="pp-section-title">BY MODE</h2>
        <div className="pp-mode-list">
          {modeCard('Quick Match', ms.quick_casual || {}, 'quick', 'goals_for')}
          {modeCard('Friend Match', ms.friend || {}, 'friend', 'friend')}
          {isMe && <PracticeVsBotCard />}
          <ModeCard testid="profile-mode-ranked" icon="wins" name="RANKED"
            major={rp ? `${fmtNum(rp.rankedMatchesPlayed)} matches` : 'No ranked matches yet'}
            minor={rp ? `${rp.wins}W ${rp.losses}L · ${fmtPct(rp.winRate)}` : null}
            extra={rp ? (rp.isPlacement ? `Placement ${rp.placementMatchesPlayed} / ${rp.placementMatchesRequired}` : `${rp.currentSR} SR · ${rp.tierLabel}`) : null} />
        </div>
      </section>
      <section data-testid="profile-stats-fun">
        <h2 className="pp-section-title pp-gap">FUN STATS</h2>
        <div className="pp-mini-stats" data-testid="profile-fun">
          {fun.map(([label, value]) => <div className="pp-mini" key={label}><div>{label}</div><strong>{value}</strong></div>)}
          <div className="pp-mini pp-mini--wide"><div>Recent form (10)</div><FormChips form={data.recentForm10} small testid="profile-fun-form10" /></div>
        </div>
        <div className="pp-note">
          More advanced stats (Joker breakdown, GK saves, playstyle) unlock as we collect enough per-action data from future matches.
        </div>
      </section>
    </div>
  );
}

// Practice vs Bot — device-local stats (localStorage via useGameEngine.recordMatch).
// Owner-only: never shown on someone else's profile, never merged with online totals.
function PracticeVsBotCard() {
  const s = storage.getStats() || {};
  const played = Number(s.matchesPlayed || 0);
  const wins = Number(s.wins || 0);
  const losses = Number(s.losses || 0);
  const winRate = played > 0 ? Math.round((wins / played) * 1000) / 10 : 0;
  if (played === 0) {
    return (
      <ModeCard testid="profile-mode-practice-empty" icon="bot" name="PRACTICE VS BOT" major="No matches yet"
        minor="Play vs Bot stats will appear here after your first practice match.">
        <div className="pp-device-only">STORED ON THIS DEVICE ONLY</div>
      </ModeCard>
    );
  }
  return (
    <article className="pp-mode" data-testid="profile-mode-practice">
      <Ico name="bot" />
      <div className="pp-mode-details">
        <div className="pp-mode-name">PRACTICE VS BOT</div>
        <div className="pp-mode-major" data-testid="profile-mode-practice-matches">{played} matches</div>
        <div className="pp-mode-minor" data-testid="profile-mode-practice-record">{wins}W {losses}L · {winRate}%</div>
        <div className="pp-mode-minor" data-testid="profile-mode-practice-totals">Goals {Number(s.totalGoals || 0)} · Saves {Number(s.totalSaves || 0)}</div>
        <div className="pp-device-only">STORED ON THIS DEVICE ONLY</div>
      </div>
    </article>
  );
}
